import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const PersonalInfoSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  isEditing,
  setIsEditing,
  onSave,
  isSaving
}) => {
  // All Delta Air Lines pilot bases
  const baseLocationOptions = [
    { value: 'ATL', label: 'Atlanta (ATL)' },
    { value: 'BOS', label: 'Boston (BOS)' },
    { value: 'DTW', label: 'Detroit (DTW)' },
    { value: 'JFK', label: 'New York JFK (JFK)' },
    { value: 'LAX', label: 'Los Angeles (LAX)' },
    { value: 'LGA', label: 'New York LaGuardia (LGA)' },
    { value: 'MSP', label: 'Minneapolis (MSP)' },
    { value: 'SEA', label: 'Seattle (SEA)' },
    { value: 'SLC', label: 'Salt Lake City (SLC)' },
  ];

  // Aircraft types matching pay_scales table codes
  const aircraftTypeOptions = [
    { value: '221', label: 'Airbus A220-100 (221)' },
    { value: '223', label: 'Airbus A220-300 (223)' },
    { value: '319', label: 'Airbus A319 (319)' },
    { value: '320', label: 'Airbus A320 (320)' },
    { value: '321', label: 'Airbus A321 (321)' },
    { value: '321N', label: 'Airbus A321neo (321N)' },
    { value: '330', label: 'Airbus A330 (330)' },
    { value: '350', label: 'Airbus A350 (350)' },
    { value: '717', label: 'Boeing 717 (717)' },
    { value: '73N', label: 'Boeing 737 (73N)' },
    { value: '757', label: 'Boeing 757 (757)' },
    { value: '763', label: 'Boeing 767-300 (763)' },
    { value: '764', label: 'Boeing 767-400 (764)' },
    { value: '777', label: 'Boeing 777 (777)' },
    { value: '787', label: 'Boeing 787 (787)' },
    { value: 'E90', label: 'Embraer E190 (E90)' },
    { value: 'E95', label: 'Embraer E195 (E95)' },
  ];

  const rankOptions = [
    { value: 'Captain', label: 'Captain' },
    { value: 'First Officer', label: 'First Officer' }
  ];

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-cyan/20 glow-subtle">
            <Icon name="User" size={20} color="var(--color-accent-cyan)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">Personal Information</h2>
            <p className="text-xs md:text-sm text-muted-foreground font-caption">
              Your profile and employment details
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-lg bg-tertiary border border-accent-cyan/20 text-accent-cyan text-sm font-medium transition-smooth hover:border-accent-cyan/60 hover:glow-subtle"
          aria-label={isEditing ? "Cancel editing" : "Edit personal information"}
        >
          <Icon name={isEditing ? "X" : "Edit"} size={16} />
          <span className="hidden sm:inline">{isEditing ? 'Cancel' : 'Edit'}</span>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Full Name"
          type="text"
          name="fullName"
          value={formData?.fullName}
          onChange={handleInputChange}
          placeholder="Enter your full name"
          disabled={!isEditing}
          required
          error={errors?.fullName}
          className="w-full"
        />

        <Input
          label="Employee ID"
          type="text"
          name="employeeId"
          value={formData?.employeeId}
          onChange={handleInputChange}
          placeholder="Enter employee ID"
          disabled
          className="w-full"
        />

        <Select
          label="Base Location"
          options={baseLocationOptions}
          value={formData?.baseLocation}
          onChange={(value) => handleInputChange({ target: { name: 'baseLocation', value } })}
          placeholder="Select your base"
          disabled={!isEditing}
          required
          error={errors?.baseLocation}
          className="w-full"
        />

        <Select
          label="Aircraft Type"
          options={aircraftTypeOptions}
          value={formData?.aircraftType}
          onChange={(value) => handleInputChange({ target: { name: 'aircraftType', value } })}
          placeholder="Select aircraft type"
          disabled={!isEditing}
          required
          error={errors?.aircraftType}
          className="w-full"
        />

        <Select
          label="Rank"
          options={rankOptions}
          value={formData?.rank}
          onChange={(value) => handleInputChange({ target: { name: 'rank', value } })}
          placeholder="Select rank"
          disabled={!isEditing}
          required
          error={errors?.rank}
          className="w-full"
        />

        <Input
          label="Hire Date"
          type="date"
          name="hireDate"
          value={formData?.hireDate}
          onChange={handleInputChange}
          disabled={!isEditing}
          required
          error={errors?.hireDate}
          className="w-full"
        />
      </div>
      {isEditing && (
        <div className="mt-6 flex flex-col gap-4">
          <div className="p-4 rounded-lg bg-accent-cyan/10 border border-accent-cyan/20">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-accent-cyan)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Employee ID cannot be changed. Contact HR for any corrections to your employment record.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 rounded-lg bg-tertiary border border-border text-muted-foreground text-sm font-medium transition-smooth hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-cyan text-background text-sm font-medium transition-smooth hover:bg-accent-cyan/90 disabled:opacity-50"
            >
              <Icon name="Save" size={16} />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoSection;